/**
 * winnerDisplay.js - Pure display helpers for game-specific winner labels/details.
 *
 * No API calls, no React hooks, no routing. Safe to reuse in:
 *   - WinnersBanner.jsx
 *   - WinnersGridSection.jsx
 *   - RecentWinners.jsx
 *   - CompactWinnersList.jsx
 */

import { formatCurrencyValue } from './formatCurrency';
import { resolvePrizeDescription } from './winnersApi';

/**
 * Returns a human-readable title for the winner based on game type.
 * @param {object} winner - Winner data object
 * @param {function} [t] - Optional t() function for i18n; if provided, returns translated text
 */
export function getWinnerTitle(winner, t) {
  if (!winner) return '';
  if (winner.additionalDrawId) {
    return t ? t('winners.winnerTitle.additionalDraw', 'Bonus Winner') : 'Bonus Winner';
  }
  const gt = winner.gameType;
  if (gt === 'chaseTheAce') {
    const isSecondary = winner.isSecondaryPrize || winner.secondaryPrize;
    // Only a confirmed jackpot win (Ace found) gets the jackpot title. A row
    // whose envelope has not been selected yet (isWinner !== true) is not a
    // jackpot winner — treat it as a weekly winner.
    const isJackpot = winner.isWinner === true && !isSecondary;
    return t
      ? t(isJackpot ? 'winners.winnerTitle.jackpotWinner' : 'winners.winnerTitle.weeklyWinner', isJackpot ? 'Jackpot Winner' : 'Weekly Winner')
      : (isJackpot ? 'Jackpot Winner' : 'Weekly Winner');
  }
  if (gt === 'superballBingo') {
    return t
      ? t(winner.isJackpot ? 'winners.winnerTitle.jackpotWinner' : 'winners.winnerTitle.sessionWinner', winner.isJackpot ? 'Jackpot Winner' : 'Session Winner')
      : (winner.isJackpot ? 'Jackpot Winner' : 'Session Winner');
  }
  if (gt === 'prizeDraw') {
    return t ? t('winners.winnerTitle.prizeWon', 'Prize Won') : 'Prize Won';
  }
  if (gt === 'progressive5050') {
    const isSecondary = !!winner.secondaryPrize;
    return t
      ? t(isSecondary ? 'winners.winnerTitle.secondaryPrize' : 'winners.winnerTitle.jackpotWinner', isSecondary ? 'Secondary Prize' : 'Jackpot Winner')
      : (isSecondary ? 'Secondary Prize' : 'Jackpot Winner');
  }
  return t
    ? t(winner.isWinner !== false ? 'winners.winnerTitle.winner' : 'winners.winnerTitle.nonWinner', winner.isWinner !== false ? 'Winner' : 'Non-Winner')
    : (winner.isWinner !== false ? 'Winner' : 'Non-Winner');
}

/**
 * Returns a short prize-category label.
 * @param {object} winner - Winner data object
 * @param {function} [t] - Optional t() function for i18n; if provided, returns translated text
 */
export function getWinnerPrizeLabel(winner, t) {
  if (!winner) return '';
  if (winner.additionalDrawId) {
    return t ? t('winners.winnerPrizeLabel.additionalDraw', 'Bonus Prize') : 'Bonus Prize';
  }
  const gt = winner.gameType;
  if (gt === 'chaseTheAce') {
    const isSecondary = winner.isSecondaryPrize || winner.secondaryPrize;
    // Only a confirmed jackpot win (Ace found) shows the Jackpot label.
    const isJackpot = winner.isWinner === true && !isSecondary;
    return t
      ? t(isJackpot ? 'winners.winnerPrizeLabel.jackpot' : 'winners.winnerPrizeLabel.weeklyPot', isJackpot ? 'Jackpot' : 'Weekly Pot')
      : (isJackpot ? 'Jackpot' : 'Weekly Pot');
  }
  if (gt === 'superballBingo') {
    return t
      ? t(winner.isJackpot ? 'winners.winnerPrizeLabel.jackpot' : 'winners.winnerPrizeLabel.session', winner.isJackpot ? 'Jackpot' : 'Session')
      : (winner.isJackpot ? 'Jackpot' : 'Session');
  }
  if (gt === 'prizeDraw') {
    return t ? t('winners.winnerPrizeLabel.prizeWon', 'Prize Won') : 'Prize Won';
  }
  if (gt === 'progressive5050') {
    const isSecondary = !!winner.secondaryPrize;
    return t
      ? t(isSecondary ? 'winners.winnerPrizeLabel.secondary' : 'winners.winnerPrizeLabel.jackpot', isSecondary ? 'Secondary' : 'Jackpot')
      : (isSecondary ? 'Secondary' : 'Jackpot');
  }
  return t
    ? t(winner.isPrize ? 'winners.winnerPrizeLabel.prizeWon' : 'winners.winnerPrizeLabel.jackpot', winner.isPrize ? 'Prize Won' : 'Jackpot')
    : (winner.isPrize ? 'Prize Won' : 'Jackpot');
}

/**
 * Resolves a localized prize description from winner metadata, if any.
 * @param {object} winner - Winner data object
 * @param {string} [language='en'] - Language key for locale-aware formatting
 */
export function getWinnerPrizeDescription(winner, language = 'en') {
  if (!winner) return null;
  if (winner.prize) return winner.prize;
  const descriptions =
    winner.prizeDescriptions || winner.prizeDescription || winner.PrizeDescriptions || null;
  return resolvePrizeDescription(descriptions, language);
}

/**
 * Returns a formatted amount string (or prize description).
 * @param {object} winner - Winner data object
 * @param {object} [options]
 * @param {string} [options.language] - Language key for locale-aware formatting
 */
export function getWinnerAmountText(winner, options = {}) {
  if (!winner) return '';
  const prize = getWinnerPrizeDescription(winner, options.language);
  if (prize) return prize;
  const isPrize = winner.gameType === 'prizeDraw' || winner.isPrize === true;
  if (isPrize) return '';
  const amount = Number(winner.amount ?? 0);
  if (amount < 0) return '';
  return formatCurrencyValue(amount, { language: options.language });
}

/**
 * True when a row represents a genuine "no winner / jackpot rolled over" draw.
 *
 * A weekly/secondary-prize winner also comes back with isWinner === false
 * (the jackpot/Ace was not found), but they DID win the weekly pot, so they
 * must NOT be shown as a rollover. Only treat as a rollover when no prize of
 * any kind was awarded.
 */
export function isRolloverWinner(winner) {
  if (!winner) return false;
  if (winner.isWinner !== false) return false;
  if (winner.isSecondaryPrize === true || winner.secondaryPrize) return false;
  if (winner.isPrize === true || winner.prize) return false;
  return true;
}

/**
 * Returns an array of { label, value } metadata items for the winner.
 * @param {object} winner - Winner data object
 * @param {function} [t] - Optional t() function for i18n
 */
export function getWinnerMetaItems(winner, t) {
  if (!winner) return [];
  const items = [];
  const label = (key, fallback) => t ? t(key, fallback) : fallback;
  if (winner.ticketNumber) items.push({ label: label('winners.winnerMeta.ticket', 'Ticket'), value: winner.ticketNumber });
  if (winner.gameType === 'chaseTheAce' && winner.gameNumber) items.push({ label: label('winners.winnerMeta.game', 'Game'), value: winner.gameNumber });
  if (winner.gameType === 'chaseTheAce' && winner.weekNumber) items.push({ label: label('winners.winnerMeta.week', 'Week'), value: winner.weekNumber });
  if (winner.gameType === 'chaseTheAce' && winner.cardDrawn) items.push({ label: label('winners.winnerMeta.card', 'Card'), value: winner.cardDrawn });
  if (winner.gameType === 'squareGame' && winner.squareNumber) items.push({ label: label('winners.winnerMeta.square', 'Square'), value: `#${winner.squareNumber}` });
  if (winner.gameType === 'superballBingo' && winner.ballSelected) items.push({ label: label('winners.winnerMeta.ball', 'Ball'), value: winner.ballSelected });
  if (winner.sessionNumber) items.push({ label: label('winners.winnerMeta.session', 'Session'), value: winner.sessionNumber });
  if (winner.date) items.push({ label: label('winners.winnerMeta.date', 'Date'), value: winner.date });
  return items;
}
